import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FEATURES } from '../config/features';
import './SharedFormStyles.css';
import './SendPackage.css';
import { CustomButton8, CustomInputBox1, CustomButton2, CustomTextArea, ModernFormSection, ModernDropdown, ModernDateTimePicker } from './Templates.js';
import {
  FaArrowLeft,
  FaCheck,
  FaLocationArrow,
  FaSearchLocation,
  FaClock,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaExpandAlt,
  FaSpinner,
  FaBox,
  FaWeightHanging,
  FaAngleDown,
  FaInfoCircle,
  FaLock,
  FaFileAlt,
  FaEuroSign
} from 'react-icons/fa';
import { supabase } from './data/supabaseClient';
import { AuthContext } from './Provider.js';
import GoogleMapsRoute from './GoogleMapsRoute';
import { generateRoutePoints } from '../utils/routeMatchingUtils';
import { getCoordinates } from '../utils/geocodingUtils';
import { storeRouteMapImage } from '../utils/storeRouteMap';
import { useTranslation } from 'react-i18next';
import { useToast } from './hooks/ToastContext';
import Spinner from './common/Spinner';

export default function SendPackage() {
  const { t } = useTranslation('common');
  const { user, profileLoading } = useContext(AuthContext);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUserDataLoading, setIsUserDataLoading] = useState(true);
  const [packageLimits, setPackageLimits] = useState(null);
  const [advanceBookingLimits, setAdvanceBookingLimits] = useState(null);
  const [dateValidationError, setDateValidationError] = useState(null);
  const navigate = useNavigate();
  const mapComponentRef = useRef();
  const { addToast } = useToast();

  // Separate state for dropdowns to avoid conflicts with the form state
  const [showSizeMenu, setShowSizeMenu] = useState(false);
  const [showWeightMenu, setShowWeightMenu] = useState(false);

  const [formData, setFormData] = useState({
    packageSize: 'Medium', // Default value
    packageWeight: 'Up to 5kg', // Default value
    description: '',
    address_from: {
      street: '',
      city: '',
      country: ''
    },
    address_to: {
      street: '',
      city: '',
      country: ''
    },
    selectedDate: new Date(),
    selectedTime: new Date(),
    static_map_url: null,
    distance_km: null,
    error: null,
    // Price field for delivery cost
    delivery_cost: '',
  });

  // Check if user has reached their package limit
  const hasReachedLimit = packageLimits && !packageLimits.has_remaining;

  // Debug state to log address values
  useEffect(() => {
    if (hasReachedLimit) return; // Don't log if reached limit
    
    console.log("Current address data:", {
      from: formData.address_from,
      to: formData.address_to
    });
  }, [formData.address_from, formData.address_to, hasReachedLimit]);

  // Initialize component with user data
  useEffect(() => {
    const checkSession = async () => {
      try {
        setIsUserDataLoading(true);
        const { data } = await supabase.auth.getSession();
        if (!data.session) {
          navigate('/login');
          return;
        }
        
        // Check package limits
        if (data.session?.user) {
          const { data: limitData, error: limitError } = await supabase.rpc('check_monthly_package_limit', {
            p_user_id: data.session.user.id
          });
          
          if (limitError) {
            console.error('Error checking package limits:', limitError);
          } else if (limitData) {
            console.log('Package limits data:', limitData);
            setPackageLimits(limitData);
          }

          // Check advance booking limits
          const { data: advanceLimitData, error: advanceLimitError } = await supabase.rpc('get_user_advance_booking_limits', {
            p_user_id: data.session.user.id
          });
          
          if (advanceLimitError) {
            console.error('Error checking advance booking limits:', advanceLimitError);
          } else if (advanceLimitData) {
            console.log('Advance booking limits data:', advanceLimitData);
            setAdvanceBookingLimits(advanceLimitData);
          }
        }
      } catch (error) {
        console.error("Error checking session:", error);
        addToast({
          type: 'error',
          title: t('errors.session_error'),
          description: t('errors.session_check_failed')
        });
        navigate('/login');
      } finally {
        setIsUserDataLoading(false);
      }
    };

    checkSession();
  }, [navigate, t, addToast]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleDocumentClick = (e) => {
      // Close dropdown menus when clicking outside
      if (showSizeMenu && !e.target.closest('.SP-size-selector')) {
        setShowSizeMenu(false);
      }
      if (showWeightMenu && !e.target.closest('.SP-weight-selector')) {
        setShowWeightMenu(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, [showSizeMenu, showWeightMenu]);

  const handleBackClick = () => {
    navigate("/home");
  };

  const handleLocationSelect = (field, value) => {
    // Don't update form data if limit reached
    if (hasReachedLimit) return;
    
    console.log(`handleLocationSelect called with field: ${field}, value:`, value);
    
    if (field === 'distance_km') {
      setFormData(prev => ({
        ...prev,
        distance_km: value
      }));
    } else if (field === 'static_map_url') {
      setFormData(prev => ({
        ...prev,
        static_map_url: value
      }));
    } else if (field === 'route_polyline') {
      setFormData(prev => ({
        ...prev,
        route_polyline: value
      }));
      console.log('Route polyline saved to form state');
    }

    // Parse the Google Places result
    if (value && value.address_components) {
      const streetNumber = value.address_components.find(c => c.types.includes('street_number'))?.long_name || '';
      const route = value.address_components.find(c => c.types.includes('route'))?.long_name || '';

      // Check for special types (airports, train stations, etc.)
      const isAirport = value.types?.includes('airport');
      const isTransitStation = value.types?.includes('transit_station') || value.types?.includes('train_station');

      const establishment = value.address_components.find(c =>
        c.types.includes('establishment') ||
        c.types.includes('point_of_interest') ||
        c.types.includes('premise') ||
        c.types.includes('airport')
      )?.long_name || '';

      // Try to get city from address components with comprehensive fallback chain
      // Priority: locality → postal_town → sublocality → administrative_area_level_2 → administrative_area_level_1
      const cityTypes = [
        'locality',                    // Primary city name (e.g., "Rīga")
        'postal_town',                 // Postal town
        'sublocality',                 // Neighborhood/district (e.g., "Latgales priekšpilsēta")
        'administrative_area_level_2', // Municipality (e.g., "Mārupe")
        'administrative_area_level_1'  // Region fallback
      ];

      let city = '';
      for (const type of cityTypes) {
        const component = value.address_components.find(c => c.types.includes(type));
        if (component?.long_name) {
          city = component.long_name;
          break;
        }
      }

      const country = value.address_components.find(c => c.types.includes('country'))?.short_name || '';

      // Build street address intelligently
      let street;

      // For airports and transit stations, prefer the establishment name or formatted_address first part
      if (isAirport || isTransitStation) {
        if (value.name) {
          // Use the full place name for airports/stations (e.g., "Riga International Airport")
          street = value.name;
        } else if (establishment) {
          street = establishment;
        } else {
          // Fallback: take the first part of formatted_address before first comma
          street = value.formatted_address.split(',')[0].trim();
        }
      } else if (establishment && route) {
        // Establishment with street: "t/c Apelsīns, Rīgas gatve 5"
        street = streetNumber ? `${establishment}, ${route} ${streetNumber}` : `${establishment}, ${route}`;
      } else if (establishment) {
        // Just establishment: "t/c Apelsīns"
        street = establishment;
      } else if (streetNumber && route) {
        // Standard address: "Rīgas gatve 5"
        street = `${route} ${streetNumber}`;
      } else if (route) {
        // Just street name: "Rīgas gatve"
        street = route;
      } else {
        // Fallback to formatted address
        street = value.formatted_address;
      }

      // If city is still empty, try to extract it from formatted_address
      if (!city && value.formatted_address) {
        // formatted_address format: "Street, City, Country" or "Establishment, Street, City, Country"
        const parts = value.formatted_address.split(',').map(p => p.trim());

        // Try to find city by checking if part is not a number and not the country code
        for (let i = 1; i < parts.length - 1; i++) {
          const part = parts[i];
          // Skip if it's a number, too short, or looks like a street
          if (part && !/^\d+$/.test(part) && part.length > 2 && !part.match(/^(gatve|iela|ceļš)/i)) {
            // Check if this part contains any address components we already extracted
            const isStreetPart = (streetNumber && part.includes(streetNumber)) ||
                                (route && part === route) ||
                                (establishment && part === establishment);

            if (!isStreetPart) {
              city = part;
              break;
            }
          }
        }
      }

      const addressField = field === 'address_from' ? 'address_from' : 'address_to';

      console.log(`Setting ${addressField} to:`, {
        street,
        city,
        country,
        formatted_address: value.formatted_address
      });

      setFormData(prev => ({
        ...prev,
        [addressField]: {
          street,
          city,
          country
        }
      }));
    } else if (field === 'address_from' || field === 'address_to') {
      // If it's a direct address string (not a Google Place object)
      console.log(`Setting ${field} with string:`, value);
      
      // Extract what might be city/country from the string if possible
      const parts = typeof value === 'string' ? value.split(',').map(p => p.trim()) : [];
      
      setFormData(prev => ({
        ...prev,
        [field]: {
          street: typeof value === 'string' ? value : '',
          city: parts.length > 1 ? parts[1] : '',
          country: parts.length > 2 ? parts[2] : ''
        }
      }));
    }
  };

  // Price utility functions
  const normalizePriceInput = (value) => {
    if (!value) return '';
    // Replace comma with dot for consistent decimal separator
    return value.replace(',', '.');
  };

  const formatPriceValue = (value) => {
    if (!value) return '';
    const normalized = normalizePriceInput(value);
    const parsed = parseFloat(normalized);
    if (isNaN(parsed)) return '';
    // Format to 2 decimals
    return parsed.toFixed(2);
  };

  const isValidPriceInput = (value) => {
    if (!value) return true;
    const normalized = normalizePriceInput(value);
    // Allow: up to 3 digits before decimal, optional decimal with up to 2 digits after
    return /^\d{0,3}([.,]\d{0,2})?$/.test(value);
  };

  const handleChange = (field, value) => {
    // Don't update form data if limit reached
    if (hasReachedLimit) return;

    // Special handling for date changes - validate advance booking limits
    if (field === 'selectedDate' || field === 'selectedTime') {
      validateAdvanceBookingDate(field, value);
    }

    setFormData(prev => ({
      ...prev,
      [field]: value,
      error: null // Clear any previous errors on field change
    }));
  };

  // Function to validate advance booking date limits
  const validateAdvanceBookingDate = async (field, value) => {
    if (!user || !advanceBookingLimits) return;

    // Get the complete date-time combination
    let checkDate, checkTime;
    if (field === 'selectedDate') {
      checkDate = value;
      checkTime = formData.selectedTime;
    } else if (field === 'selectedTime') {
      checkDate = formData.selectedDate;
      checkTime = value;
    }

    if (!checkDate || !checkTime) return;

    // Format date and time for the API call
    const travelDate = checkDate.toISOString().split('T')[0]; // YYYY-MM-DD format
    const travelTime = checkTime.toTimeString().split(' ')[0]; // HH:MM:SS format

    try {
      const { data: validationResult, error } = await supabase.rpc('check_advance_booking_limit', {
        p_user_id: user.id,
        p_travel_date: travelDate,
        p_travel_time: travelTime
      });

      if (error) {
        console.error('Error validating advance booking date:', error);
        return;
      }

      if (!validationResult.can_create) {
        setDateValidationError({
          message: validationResult.error,
          maxDays: validationResult.max_advance_days,
          userPlan: validationResult.user_plan,
          daysInAdvance: validationResult.days_in_advance
        });
      } else {
        setDateValidationError(null);
      }
    } catch (err) {
      console.error('Exception validating advance booking date:', err);
    }
  };

  const formatTimeString = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const validateForm = () => {
    // Don't validate if limits reached
    if (hasReachedLimit) return false;
    
    // Check for advance booking date validation errors
    if (dateValidationError) {
      setFormData(prev => ({ 
        ...prev, 
        error: dateValidationError.message
      }));
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: dateValidationError.message
      });
      return false;
    }
    
    console.log("Validating form with addresses:", formData.address_from, formData.address_to);
    
    // Properly check if both addresses are provided (both street fields must be non-empty)
    if (!formData.address_from.street || !formData.address_to.street) {
      console.log("Address validation failed: Missing street in from or to");
      setFormData(prev => ({ 
        ...prev, 
        error: t('forms.address_required')
      }));
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: t('forms.address_required')
      });
      return false;
    }

    // Check if the route has been calculated by checking distance_km
    if (!formData.distance_km) {
      console.log("Route validation failed: Route not calculated");
      setFormData(prev => ({
        ...prev,
        error: t('forms.calculate_route')
      }));
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: t('forms.calculate_route')
      });
      return false;
    }

    console.log("Form validation passed");
    return true;
  };

  const handlePublishPackage = async () => {
    // Don't proceed if limits reached
    if (hasReachedLimit) return;
    
    try {
      // Basic validation
      if (!validateForm()) {
        return;
      }

      setIsPublishing(true);
      
      // Check subscription limits for package publishing
      const { data: limitData, error: limitError } = await supabase.rpc('check_monthly_package_limit', {
        p_user_id: user.id
      });
      
      if (limitError) {
        console.error('Error checking package limits:', limitError);
        throw new Error('Could not verify your subscription limits');
      }
      
      if (!limitData.has_remaining) {
        const isDaily = limitData.limit_type === 'daily';
        if (isDaily) {
          addToast({
            type: 'error',
            title: t('errors.limit_reached'),
            description: t('package_forms.daily_package_limit_reached', { max: limitData.daily_max || 10 })
          });
        } else if (limitData.max_allowed === 0) {
          addToast({
            type: 'error',
            title: t('package_forms.subscription_required'),
            description: t('package_forms.subscription_required_message')
          });
        } else {
          addToast({
            type: 'error',
            title: t('errors.limit_reached'),
            description: t('package_forms.limit_reached_description', { max: limitData.max_allowed })
          });
        }

        // Only offer a plan upgrade for the (paywalled) monthly limit, not the daily cap.
        if (!isDaily && FEATURES.PAYMENTS_ENABLED && window.confirm(t('package_forms.view_subscription_plans_confirm'))) {
          navigate('/subscription-plans');
          return;
        }

        setIsPublishing(false);
        setPackageLimits(limitData);
        return;
      }
      
      // Update our local state with the fetched limits
      setPackageLimits(limitData);
      
      // Geocode addresses to get coordinates for location alerts
      let fromCoords = null;
      let toCoords = null;
      
      try {
        // Get coordinates for both addresses
        const fromAddress = `${formData.address_from.street}, ${formData.address_from.city}, ${formData.address_from.country}`.trim();
        const toAddress = `${formData.address_to.street}, ${formData.address_to.city}, ${formData.address_to.country}`.trim();
        
        console.log('Geocoding FROM address:', fromAddress);
        const fromResult = await getCoordinates(fromAddress);
        
        if (fromResult.success) {
          fromCoords = fromResult.data;
          console.log('✅ FROM coordinates:', fromCoords);
        } else {
          console.warn('⚠️ Failed to geocode FROM address:', fromResult.error);
        }
        
        console.log('Geocoding TO address:', toAddress);
        const toResult = await getCoordinates(toAddress);
        
        if (toResult.success) {
          toCoords = toResult.data;
          console.log('✅ TO coordinates:', toCoords);
        } else {
          console.warn('⚠️ Failed to geocode TO address:', toResult.error);
        }
      } catch (geocodeError) {
        console.error('Geocoding error (non-critical):', geocodeError);
        // Don't block package creation if geocoding fails
        // Location alerts just won't work for this package
      }

      // Store the route map image once (Supabase Storage) so the feed serves
      // our stored copy instead of re-billing Google Static Maps per card view.
      const storedMapUrl = await storeRouteMapImage(formData.static_map_url, user.id);

      // Create package data
      const packageData = {
        user_id: user.id,
        package_size: formData.packageSize,
        package_weight: formData.packageWeight,
        travel_date: formData.selectedDate,
        travel_time: formatTimeString(formData.selectedTime),
        travel_type: "One-time", // Removed daily option
        map_image_url: storedMapUrl,
        route_polyline: formData.route_polyline, // Add route polyline for matching
        search_radius_km: 10, // Default search radius of 10km
        distance_km: formData.distance_km,
        address_from_street: formData.address_from.street,
        address_from_city: formData.address_from.city,
        address_from_country: formData.address_from.country,
        address_to_street: formData.address_to.street,
        address_to_city: formData.address_to.city,
        address_to_country: formData.address_to.country,
        description: formData.description,
        // Coordinates for location alerts (optional - package still works without them)
        from_latitude: fromCoords?.latitude || null,
        from_longitude: fromCoords?.longitude || null,
        from_place_id: fromCoords?.place_id || null,
        to_latitude: toCoords?.latitude || null,
        to_longitude: toCoords?.longitude || null,
        to_place_id: toCoords?.place_id || null,
        status: "active", // Ensure the package is active
        // Delivery cost field
        delivery_cost: formData.delivery_cost ? parseFloat(formData.delivery_cost) : null,
      };

      console.log("Publishing package with data:", packageData);

      // Validate advance booking limits one more time before creating
      const travelDate = formData.selectedDate.toISOString().split('T')[0];
      const travelTime = formatTimeString(formData.selectedTime);
      
      const { data: advanceValidation, error: advanceError } = await supabase.rpc('check_advance_booking_limit', {
        p_user_id: user.id,
        p_travel_date: travelDate,
        p_travel_time: travelTime
      });
      
      if (advanceError) {
        console.error('Error validating advance booking limit:', advanceError);
        throw new Error('Could not validate booking date');
      }
      
      if (!advanceValidation.can_create) {
        throw new Error(advanceValidation.error);
      }

      // Create the package
      const { data: newPackage, error: packageError } = await supabase
        .from('packages')
        .insert(packageData)
        .select()
        .single();
  
      if (packageError) throw packageError;
      
      // If we have a polyline, generate route points
      if (formData.route_polyline && newPackage) {
        try {
          // Use our utility function to generate route points from the polyline
          const result = await generateRoutePoints(formData.route_polyline, {
            postId: null,
            packageId: newPackage.id,
            intervalKm: 5 // Sample points every 5km
          });

          if (!result.success) {
            console.error('Error generating route points:', result.error);
            // Don't throw an error here - we'll continue even if route points generation fails
          } else {
            console.log('Route points generated successfully for package ID:', newPackage.id);
          }
        } catch (routeError) {
          console.error('Exception in route points generation:', routeError);
          // Don't throw an error here - we'll continue even if route points generation fails
        }
      }
      
      // Increment the monthly package count
      const { data: incrementData, error: incrementError } = await supabase.rpc('increment_monthly_package_count', {
        p_user_id: user.id
      });
      
      if (incrementError) {
        console.error('Error incrementing package count:', incrementError);
        // Non-critical error, just log it
      } else if (incrementData) {
        // Update our local state with the new count
        setPackageLimits(incrementData);
      }
      
      addToast({
        type: 'success',
        title: t('forms.publish_success'),
        description: t('package_forms.published_successfully')
      });

      // Show how many packages they have left this month
      if (incrementData && incrementData.max_allowed < 9999) {
        addToast({
          type: 'info',
          title: t('package_forms.package_published'),
          description: t('package_forms.packages_remaining_info', { 
            count: incrementData.remaining,
            remaining: incrementData.remaining 
          })
        });
      }

      navigate('/home');
    } catch (error) {
      console.error('Error publishing package:', error);

      const isDailyCap = (error?.message || '').includes('DAILY_PACKAGE_LIMIT_REACHED');
      const description = isDailyCap
        ? t('package_forms.daily_package_limit_reached', { max: 10 })
        : (error.message || t('forms.publish_error'));

      setFormData(prev => ({ ...prev, error: description }));
      setIsPublishing(false);

      addToast({
        type: 'error',
        title: isDailyCap ? t('errors.limit_reached') : t('errors.error'),
        description
      });
    } finally {
      setIsPublishing(false);
    }
  };

  // Handle dropdown selections
  const handleSizeSelect = (size) => {
    if (hasReachedLimit) return;
    
    // Map translated size back to English for storage
    const sizeMapping = {
      [t('package_forms.size_small')]: 'Small',
      [t('package_forms.size_medium')]: 'Medium',
      [t('package_forms.size_large')]: 'Large',
      [t('package_forms.size_extra_large')]: 'Extra Large'
    };
    
    const englishSize = sizeMapping[size] || size;
    setFormData(prev => ({ ...prev, packageSize: englishSize }));
    setShowSizeMenu(false);
  };

  const handleWeightSelect = (weight) => {
    if (hasReachedLimit) return;
    
    // Map translated weight back to English for storage
    const weightMapping = {
      [t('package_forms.weight_1kg')]: 'Up to 1kg',
      [t('package_forms.weight_5kg')]: 'Up to 5kg',
      [t('package_forms.weight_10kg')]: 'Up to 10kg',
      [t('package_forms.weight_20kg')]: 'Up to 20kg',
      [t('package_forms.weight_over_20kg')]: 'Over 20kg'
    };
    
    const englishWeight = weightMapping[weight] || weight;
    setFormData(prev => ({ ...prev, packageWeight: englishWeight }));
    setShowWeightMenu(false);
  };

  // Toggle dropdown states
  const toggleSizeMenu = (e) => {
    if (hasReachedLimit) return;
    e.stopPropagation();
    setShowSizeMenu(!showSizeMenu);
    setShowWeightMenu(false);
  };

  const toggleWeightMenu = (e) => {
    if (hasReachedLimit) return;
    e.stopPropagation();
    setShowWeightMenu(!showWeightMenu);
    setShowSizeMenu(false);
  };

  // Add loading spinner for initial loading or profile loading
  if (isUserDataLoading || profileLoading) {
    return (
      <div className="SP-loading-container">
        <Spinner size="medium" />
        <p>{profileLoading ? t('app.loading_profile') : t('app.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`SP-publish-post-container ${hasReachedLimit ? 'SP-limit-reached' : ''}`}>
        <div className="SP-back-button">
          <CustomButton8 onClick={handleBackClick}>
            <FaArrowLeft />
          </CustomButton8>
        </div>

        <div className="SP-form-header">
          <h2 className="SP-form-heading">{t('package_forms.send_package')}</h2>
          <p className="SP-form-subtitle">{t('package_forms.connect_travelers')}</p>
        </div>

        <div className="SP-form-content">

        {/* Daily cap notice — shown even while payments are paused (applies to all). */}
        {packageLimits?.limit_type === 'daily' && (
          <div className="SP-package-limits-info SP-limit-reached">
            <FaExclamationTriangle />
            <span>{t('package_forms.daily_package_limit_reached', { max: packageLimits.daily_max || 10 })}</span>
          </div>
        )}

        {/* Package Limits Info — hidden while payments are paused (everyone unlimited) */}
        {FEATURES.PAYMENTS_ENABLED && packageLimits && (
          <div className={`SP-package-limits-info ${packageLimits.has_remaining ? '' : 'SP-limit-reached'}`}>
            {packageLimits.max_allowed === 0 ? (
              <div className="SP-free-plan-package-message">
                <FaExclamationTriangle />
                <span>{t('package_forms.subscription_required_message')}</span>
                <div className="SP-upgrade-button-container">
                  <CustomButton2 
                    label={t('package_forms.upgrade_plan')} 
                    onClick={() => navigate('/subscription-plans')}
                  />
                </div>
              </div>
            ) : packageLimits.max_allowed >= 9999 ? (
              <span>{t('package_forms.unlimited_packages')}</span>
            ) : (
              <>
                <span>
                  {t('package_forms.packages_remaining', { remaining: packageLimits.remaining, max: packageLimits.max_allowed })}
                </span>
                {packageLimits.next_reset_date && (
                  <span className="reset-date">{t('package_forms.resets_on', { date: new Date(packageLimits.next_reset_date).toLocaleDateString() })}</span>
                )}
                {!packageLimits.has_remaining && (
                  <div className="SP-upgrade-button-container">
                    <CustomButton2 
                      label={t('package_forms.upgrade_plan')} 
                      onClick={() => navigate('/subscription-plans')}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Advance Booking Limits Info — mentions the plan, hidden while payments paused */}
        {FEATURES.PAYMENTS_ENABLED && advanceBookingLimits && (
          <div className="SP-advance-booking-info">
            <div className="SP-info-header">
              <FaCalendarAlt />
              <span>{t('package_forms.advance_booking_limit')}</span>
            </div>
            <div className="SP-info-content">
              <span>{t('package_forms.advance_booking_description', {
                plan: advanceBookingLimits.plan_name || 'Basic',
                days: advanceBookingLimits.max_advance_days || 6
              })}</span>
              <span className="SP-max-date">{t('package_forms.maximum_date', { date: new Date(advanceBookingLimits.max_date).toLocaleDateString() })}</span>
            </div>
          </div>
        )}

        {/* Date Validation Error */}
        {dateValidationError && (
          <div className="SP-date-validation-error">
            <FaExclamationTriangle />
            <div className="SP-error-content">
              <p>{dateValidationError.message}</p>
              {FEATURES.PAYMENTS_ENABLED && (
                <>
                  <p className="SP-error-details">
                    {t('forms.plan_allows_booking_days', { plan: dateValidationError.userPlan, maxDays: dateValidationError.maxDays })}
                    {' '}
                    {t('forms.tried_booking_days', { daysInAdvance: dateValidationError.daysInAdvance })}
                  </p>
                  <CustomButton2
                    label={t('package_forms.upgrade_plan')}
                    onClick={() => navigate('/subscription-plans')}
                  />
                </>
              )}
            </div>
          </div>
        )}

        {/* Display limit reached overlay */}
        {hasReachedLimit && (
          <div className="SP-limit-overlay">
            <div className="SP-limit-overlay-content">
              <FaLock className="limit-overlay-icon" />
              <h3 className="SP-limit-overlay-title">
                {packageLimits && packageLimits.max_allowed === 0 
                  ? t('package_forms.subscription_required') 
                  : t('package_forms.monthly_limit_reached')}
              </h3>
              <p className="SP-limit-overlay-text">
                {packageLimits && packageLimits.max_allowed === 0 
                  ? t('package_forms.subscription_required_message') 
                  : t('package_forms.monthly_limit_text', { 
                      max: packageLimits.max_allowed, 
                      date: new Date(packageLimits.next_reset_date).toLocaleDateString() 
                    })}
              </p>
              <CustomButton2 
                label={t('package_forms.upgrade_plan')} 
                onClick={() => navigate('/subscription-plans')}
              />
            </div>
          </div>
        )}

        {formData.error && (
          <div className="SP-error-message">
            <FaExclamationTriangle />
            <span>{formData.error}</span>
          </div>
        )}

        <ModernFormSection
          icon={<FaBox />}
          title={t('package_forms.package_details')}
        >
          <div className="SP-additional-options">
            <ModernDropdown
              label={t('package_forms.package_size')}
              value={(() => {
                const sizeToTranslation = {
                  'Small': t('package_forms.size_small'),
                  'Medium': t('package_forms.size_medium'),
                  'Large': t('package_forms.size_large'),
                  'Extra Large': t('package_forms.size_extra_large')
                };
                return sizeToTranslation[formData.packageSize] || formData.packageSize;
              })()}
              options={[
                t('package_forms.size_small'),
                t('package_forms.size_medium'),
                t('package_forms.size_large'),
                t('package_forms.size_extra_large')
              ]}
              onSelect={handleSizeSelect}
              icon={<FaExpandAlt />}
              disabled={hasReachedLimit}
            />

            <ModernDropdown
              label={t('package_forms.package_weight')}
              value={(() => {
                const weightToTranslation = {
                  'Up to 1kg': t('package_forms.weight_1kg'),
                  'Up to 5kg': t('package_forms.weight_5kg'),
                  'Up to 10kg': t('package_forms.weight_10kg'),
                  'Up to 20kg': t('package_forms.weight_20kg'),
                  'Over 20kg': t('package_forms.weight_over_20kg')
                };
                return weightToTranslation[formData.packageWeight] || formData.packageWeight;
              })()}
              options={[
                t('package_forms.weight_1kg'),
                t('package_forms.weight_5kg'),
                t('package_forms.weight_10kg'),
                t('package_forms.weight_20kg'),
                t('package_forms.weight_over_20kg')
              ]}
              onSelect={handleWeightSelect}
              icon={<FaWeightHanging />}
              disabled={hasReachedLimit}
            />
          </div>
        </ModernFormSection>

        <ModernFormSection
          icon={<FaLocationArrow />}
          title={t('forms.travel_route')}
        >
          <div className="SP-map-container2">
            <GoogleMapsRoute
              ref={mapComponentRef}
              onLocationSelect={handleLocationSelect}
              disabled={hasReachedLimit}
            />
          </div>
        </ModernFormSection>

        <ModernFormSection
          icon={<FaCalendarAlt />}
          title={t('package_forms.need_deliver_at')}
        >
          <ModernDateTimePicker
            dateValue={formData.selectedDate}
            timeValue={formData.selectedTime}
            onDateChange={date => handleChange('selectedDate', date)}
            onTimeChange={time => handleChange('selectedTime', time)}
            disabled={hasReachedLimit}
          />
        </ModernFormSection>

        <ModernFormSection
          icon={<FaFileAlt />}
          title={t('package_forms.description')}
        >
          <div className="SP-description-container">
            <CustomTextArea
              className="wsform-input"
              placeholder={t('package_forms.description_placeholder')}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              maxLength={500}
              rows={4}
              disabled={hasReachedLimit}
            />
          </div>
        </ModernFormSection>

        {/* Delivery Cost Section */}
        <ModernFormSection
          icon={<FaEuroSign />}
          title={t('forms.delivery_cost_offered')}
        >
          <div className="SP-price-input-wrapper">
            <CustomInputBox1
              className="wsform-input"
              placeholder={t('forms.delivery_cost_placeholder')}
              value={formData.delivery_cost}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || isValidPriceInput(value)) {
                  handleChange('delivery_cost', normalizePriceInput(value));
                }
              }}
              onBlur={(e) => {
                // Auto-format to 2 decimals on blur
                const formatted = formatPriceValue(e.target.value);
                handleChange('delivery_cost', formatted);
              }}
              type="text"
              inputMode="decimal"
              disabled={hasReachedLimit}
            />
            <p className="SP-field-description" style={{ marginTop: '8px', fontSize: '0.9em', color: 'var(--secondaryColor)', opacity: 0.8 }}>
              {t('forms.delivery_cost_description')}
            </p>
          </div>
        </ModernFormSection>

          <div className="SP-publish-button-container">
            <CustomButton2 
              label={isPublishing ? t('package_forms.publishing') : t('package_forms.publish')} 
              onClick={handlePublishPackage}
              disabled={isPublishing || hasReachedLimit}
            >
              {isPublishing ? <Spinner size="small" showText={false} /> : <FaCheck />}
            </CustomButton2>
            {hasReachedLimit && packageLimits.max_allowed > 0 && (
              <div className="SP-limit-message">
                <FaExclamationTriangle />
                <span>{t('package_forms.reached_monthly_limit')}</span>
              </div>
            )}
          </div>
        </div>
      </div>
  );
}
